import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { useCreateTask, useUpdateTask } from "@api/task.api";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { markEventBlocked } from "@design-system/ViewTable/ViewTable.lib";
import { Task, TaskType } from "@common/types/task";
import { PlusIcon } from "@icons";
import classNames from "classnames";
import { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { TaskRowViewers } from "@components/presence/TaskRowViewers";
import { OneClickStatus } from "../../OneClickStatus/OneClickStatus";
import { TaskBlockedBadge } from "../../TaskBlockedBadge/TaskBlockedBadge";
import { useOpenTaskSection } from "../../useOpenTaskSection";
import { getBlockedByLabel } from "../../taskBlockers";
import { useTaskListOpenTask } from "../../taskList.context";
import { NestedTaskRow } from "../../types";
import { TaskStatusTerminationConfirmationModal } from "../../TaskStatusTerminationConfirmationModal";
import { useTaskStatusChange } from "../../useTaskStatusChange";
import styles from "./TaskViewCells.module.scss";

export const TitleCell = ({
    row,
    getValue,
}: CellContext<ViewTableFeatures, NestedTaskRow, string>) => {
    const task = row.original as Task;
    const { taskTitleInlineEdit } = useTypedFlags();
    const createTask = useCreateTask();
    const updateTask = useUpdateTask(task);
    const [isEditing, setIsEditing] = useState(row.original.isNewPlaceholder);
    const [editValue, setEditValue] = useState(getValue() ?? "");
    const inputRef = useRef<HTMLInputElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const openTask = useTaskListOpenTask();
    const openTaskDependencies = useOpenTaskSection(openTask, "dependencies");

    const { handleStatusChange, confirmation } = useTaskStatusChange(task);

    const handleSave = useCallback(() => {
        const newTitle = editValue.trim();
        if (newTitle && newTitle !== getValue()) {
            if (row.original.isNewPlaceholder) {
                createTask.mutate({
                    title: newTitle,
                    type: TaskType.QUESTION,
                    assigneeId: task.assigneeId,
                    priority: task.priority,
                    status: task.status,
                    dueDate: task.dueDate,
                    groupId: task.groupId,
                    closeStageId: task.closeStageId,
                    closeId: task.closeId,
                    entityIds: task.entityIds ?? undefined,
                });
                setEditValue("");
                if (row.original.groupKey && row.original.onSetFocusedGroup) {
                    row.original.onSetFocusedGroup(row.original.groupKey);
                }
            } else {
                updateTask.mutate({ title: newTitle });
                setIsEditing(false);
            }
        } else {
            row.original.removePlaceholder?.();
            row.original.onSetFocusedGroup?.(null);
            setIsEditing(false);
        }
    }, [
        editValue,
        getValue,
        row.original,
        createTask,
        task.assigneeId,
        task.priority,
        task.status,
        task.dueDate,
        task.groupId,
        task.closeStageId,
        task.closeId,
        task.entityIds,
        updateTask,
    ]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
            } else if (e.key === "Escape") {
                setEditValue(getValue() ?? "");
                row.original.removePlaceholder?.();
                row.original.onSetFocusedGroup?.(null);
                setIsEditing(false);
            }
        },
        [handleSave, getValue, row.original],
    );

    useEffect(() => {
        setEditValue(getValue() ?? "");
    }, [getValue]);

    useEffect(() => {
        if (inputRef.current && measureRef.current) {
            const width = measureRef.current.offsetWidth;
            inputRef.current.style.width = `${Math.max(width + 3, 48)}px`;
        }
    }, [editValue, isEditing]);

    const tasksCount = useMemo(
        () =>
            (row.original.tasks ?? []).filter(
                (innerTask) => !innerTask.isNewPlaceholder,
            ).length,
        [row.original.tasks],
    );

    const statusTerminationModal = (
        <TaskStatusTerminationConfirmationModal {...confirmation} />
    );

    const blockedByLabel = getBlockedByLabel(task);
    const blockedBadge =
        (task.numberOfActiveBlockedByTasks ?? 0) > 0 ? (
            <TaskBlockedBadge
                label={blockedByLabel}
                onClick={(e) => {
                    markEventBlocked(e);
                    openTaskDependencies(task);
                }}
            />
        ) : null;

    const viewers =
        task.workspaceId && !row.original.isNewPlaceholder ? (
            <TaskRowViewers taskId={task.id} workspaceId={task.workspaceId} />
        ) : null;

    const groupTitle = !task.workspaceId ? (
        <div className={styles.groupTitleContainer}>
            {row.original.titleNode ?? getValue()}
            {tasksCount ? (
                <span className={styles.counter}>{tasksCount}</span>
            ) : null}
            {row.original.onAddPlaceholder && (
                <Tooltip content={`Add ${getValue()} task`}>
                    <Button
                        variant="secondary"
                        size="sm"
                        iconOnly
                        aria-label={`Add ${getValue()} task`}
                        onMouseDown={(e) => {
                            e.preventDefault();
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!row.getIsExpanded()) {
                                row.toggleExpanded();
                            }
                            row.original.onAddPlaceholder?.(row.original.id);
                        }}
                        className={styles.addPlaceholderButton}
                    >
                        <PlusIcon />
                    </Button>
                </Tooltip>
            )}
        </div>
    ) : null;

    if (!taskTitleInlineEdit && !row.original.isNewPlaceholder) {
        return (
            <>
                <ViewTableCell className={styles.titleCell}>
                    <div className={styles.titleCell}>
                        {row.original.status ? (
                            <span onClick={markEventBlocked}>
                                <OneClickStatus
                                    value={row.original.status}
                                    onChange={handleStatusChange}
                                />
                            </span>
                        ) : null}
                        {task.workspaceId ? (
                            <>
                                <BreakableText
                                    text={getValue()}
                                    copyId={task.id}
                                    copyLabel="Task"
                                />
                                {blockedBadge}
                                {viewers}
                            </>
                        ) : (
                            groupTitle
                        )}
                    </div>
                </ViewTableCell>
                {statusTerminationModal}
            </>
        );
    }

    return (
        <>
            <ViewTableCell captureClicks className={styles.titleCell}>
                <span
                    ref={measureRef}
                    className={styles.measureSpan}
                    aria-hidden="true"
                >
                    {editValue || "Create task"}
                </span>
                <div className={styles.titleCell}>
                    {row.original.status ? (
                        <OneClickStatus
                            value={row.original.status}
                            onChange={handleStatusChange}
                            disabled={row.original.isNewPlaceholder}
                        />
                    ) : null}
                    {task.workspaceId && (
                        <>
                            {isEditing ? (
                                <Field.Root className={styles.titleInput}>
                                    <Input
                                        variant="ghost"
                                        id={`task-input-${task.id}`}
                                        value={editValue}
                                        onChange={(event) =>
                                            setEditValue(event.target.value)
                                        }
                                        onBlur={handleSave}
                                        onKeyDown={handleKeyDown}
                                        ref={inputRef}
                                        onFocus={(event) => {
                                            if (row.original.isNewPlaceholder) {
                                                event.target.select();
                                            }
                                        }}
                                        autoFocus={row.original.shouldAutoFocus}
                                        placeholder={
                                            row.original.isNewPlaceholder
                                                ? "Create task"
                                                : ""
                                        }
                                    />
                                </Field.Root>
                            ) : (
                                <>
                                    <div
                                        className={classNames(
                                            styles.titleText,
                                            viewTableStyles.taskTitleCell,
                                        )}
                                        onClick={() => {
                                            if (task.workspaceId) {
                                                setIsEditing(true);
                                                setTimeout(() => {
                                                    inputRef.current?.focus();
                                                }, 10);
                                            }
                                        }}
                                    >
                                        <BreakableText
                                            text={getValue()}
                                            copyId={task.id}
                                            copyLabel="Task"
                                        />
                                    </div>
                                    {blockedBadge}
                                    {viewers}
                                </>
                            )}
                        </>
                    )}
                    {groupTitle}
                </div>
            </ViewTableCell>
            {statusTerminationModal}
        </>
    );
};
